import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Modal,
  Fade,
  Backdrop
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { PRICING, formatPrice } from '../utils/pricing.ts';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
}));

const StyledTitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
}));

const StyledContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledImageGrid = styled(Grid)(({ theme }) => ({
  height: '200px',
  order: 1,
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    height: '300px',
    order: 2,
  },
}));

const StyledImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  width: '100%',
  cursor: 'pointer',
  '&:hover .zoom-overlay': {
    opacity: 1,
  },
}));

const StyledServiceImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: theme.spacing(2),
}));

const StyledZoomOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const StyledZoomIcon = styled(ZoomInIcon)(({ theme }) => ({
  color: 'white',
  fontSize: '2rem',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxHeight: '90vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[24],
  padding: theme.spacing(2),
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    width: '80%',
  },
  [theme.breakpoints.up('md')]: {
    width: '70%',
  },
}));

const StyledModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const StyledModalContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
}));

const StyledModalImage = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: 'calc(90vh - 80px)',
  objectFit: 'contain',
}));

const StyledDescriptionGrid = styled(Grid)(({ theme }) => ({
  order: 2,
  [theme.breakpoints.up('md')]: {
    order: 1,
  },
}));

interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  detailedDescription: string;
  image: string;
}

interface ServiceDialogProps {
  open: boolean;
  onClose: () => void;
  service: Service | null;
}

export const ServiceDialog: React.FC<ServiceDialogProps> = ({ open, onClose, service }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [imageModalOpen, setImageModalOpen] = useState(false);

  if (!service) return null;

  const handleOpenImageModal = () => {
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        aria-labelledby={`dialog-title-${service.title}`}
        aria-describedby={`dialog-description-${service.title}`}
      >
        <StyledDialogTitle id={`dialog-title-${service.title}`}>
          <StyledTitleContainer>
            {service.icon}
            <Typography variant="h6">{service.title}</Typography>
          </StyledTitleContainer>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={onClose} 
            aria-label="Sluit dialoog"
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        
        <StyledDialogContent id={`dialog-description-${service.title}`}>
          <StyledContentBox>
            <Grid container spacing={3}>
              <StyledDescriptionGrid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  {service.detailedDescription}
                </Typography>
              </StyledDescriptionGrid>
              
              <StyledImageGrid item xs={12} md={6}>
                <StyledImageContainer
                  onClick={handleOpenImageModal}
                  role="button"
                  aria-label={`Vergroot afbeelding van ${service.title}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleOpenImageModal();
                    }
                  }}
                >
                  <StyledServiceImage
                    src={service.image}
                    alt={service.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://via.placeholder.com/400x300?text=Pippaya+Huisdieropvang';
                    }}
                  />
                  <StyledZoomOverlay className="zoom-overlay">
                    <StyledZoomIcon />
                  </StyledZoomOverlay>
                </StyledImageContainer>
              </StyledImageGrid>
            </Grid>
          </StyledContentBox>
        </StyledDialogContent>
        
        <StyledDialogActions>
          <Button 
            onClick={onClose} 
            variant="contained" 
            color="secondary"
            aria-label="Sluit dialoog"
          >
            Sluiten
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      {/* Image Modal */}
      <Modal
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        aria-labelledby="image-modal-title"
      >
        <Fade in={imageModalOpen}>
          <StyledModalBox>
            <StyledModalHeader>
              <IconButton onClick={handleCloseImageModal} aria-label="Sluit afbeelding">
                <CloseIcon />
              </IconButton>
            </StyledModalHeader>
            <StyledModalContent>
              <StyledModalImage
                src={service.image}
                alt={service.title}
              />
            </StyledModalContent>
          </StyledModalBox>
        </Fade>
      </Modal>
    </>
  );
}; 